import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';
import { Plus, Pencil, Trash2, History, UserPlus, Upload, Calendar, MapPin, ArrowLeftRight, AlertCircle, ExternalLink } from 'lucide-react';
import { format, startOfToday } from 'date-fns';
import { PERMISSIONS } from '@pms/shared';
import {
  createDmActivitySchema, updateDmActivitySchema,
} from '@pms/shared';
import type {
  CreateDmActivityInput, UpdateDmActivityInput, UpdateDmActivityStatusInput,
  AssignDmActivityInput, UploadDmActivityInput, ScheduleDmActivityInput,
} from '@pms/shared';
import { dmTeamService } from '@/services/dm-team.service';
import type { DmActivity, DmActivityStatus, DmActivityHistory } from '@/services/dm-team.service';
import { usePermission } from '@/hooks/usePermission';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { isActivityLocked, CANCEL_CONFIRM_TITLE, CANCEL_CONFIRM_TEXT } from './activityLock';
import { isHttpUrl } from './uploadLink';

function localToISO(v: string): string {
  if (!v) return v;
  return new Date(v.length === 16 ? v + ':00' : v).toISOString();
}

// ── Edit Activity Dialog ───────────────────────────────────────────────────────

const editActivityFormSchema = updateDmActivitySchema.extend({
  date_time: z.string().min(1, 'Date & time is required'),
});
type EditActivityFormData = z.infer<typeof editActivityFormSchema>;

function EditActivityDialog({
  activity, open, onClose, shootingCategories, pages, onSubmit, pending,
}: {
  activity: DmActivity;
  open: boolean;
  onClose: () => void;
  shootingCategories: any[];
  pages: any[];
  onSubmit: (body: UpdateDmActivityInput) => void;
  pending: boolean;
}) {
  const form = useForm<EditActivityFormData>({
    resolver: zodResolver(editActivityFormSchema),
    defaultValues: {
      title: activity.title,
      shooting_category_id: activity.shooting_category?.id ?? '',
      location: activity.location ?? '',
      date_time: activity.date_time
        ? format(new Date(activity.date_time), "yyyy-MM-dd'T'HH:mm")
        : '',
      updated_page_id: activity.updated_page?.id ?? activity.page?.id ?? '',
    },
  });

  const catId = form.watch('shooting_category_id');
  const requiresLocation = shootingCategories.find(c => c.id === catId)?.requires_location ?? false;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit Activity</DialogTitle></DialogHeader>
        <form
          onSubmit={form.handleSubmit(d =>
            onSubmit({ ...d, date_time: localToISO(d.date_time) } as UpdateDmActivityInput)
          )}
          className="space-y-4"
        >
          <div>
            <Label>Title *</Label>
            <Input {...form.register('title')} className="mt-1" />
            {form.formState.errors.title && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.title.message}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select
                defaultValue={activity.shooting_category?.id ?? ''}
                onValueChange={v => form.setValue('shooting_category_id', v)}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {shootingCategories.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date & Time *</Label>
              <DateTimePicker
                value={form.watch('date_time') || null}
                onChange={v => form.setValue('date_time', v || '', { shouldValidate: true })}
                minDate={startOfToday()}
              />
              {form.formState.errors.date_time && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.date_time.message}</p>
              )}
            </div>
          </div>
          <div>
            <Label>Location {requiresLocation ? '*' : '(optional)'}</Label>
            <Input {...form.register('location')} className="mt-1" placeholder="Location / NAS path…" />
            {form.formState.errors.location && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.location.message}</p>
            )}
          </div>
          <div>
            <Label>Page Section</Label>
            <Select
              defaultValue={activity.updated_page?.id ?? activity.page?.id ?? ''}
              onValueChange={v => form.setValue('updated_page_id', v)}
            >
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select page…" /></SelectTrigger>
              <SelectContent>
                {pages.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={pending || (requiresLocation && !form.watch('location')?.trim())}>Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Status helpers ─────────────────────────────────────────────────────────────

/**
 * Page tabs; each is a server-side `bucket`, so pagination counts match. A pipeline —
 * every activity is in exactly one: Active → Complete → Schedule → Upload, plus Not Completed.
 */
type ActivityTab = 'active' | 'completed' | 'scheduled' | 'uploaded' | 'not_completed';

const TAB_EMPTY_TEXT: Record<ActivityTab, string> = {
  active:        'No activities found',
  completed:     'Nothing waiting to be scheduled',
  scheduled:     'Nothing scheduled yet',
  uploaded:      'Nothing uploaded yet',
  not_completed: 'No overdue activities',
};

const STATUS_LABELS: Record<DmActivityStatus, string> = {
  pending: 'Pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<DmActivityStatus, string> = {
  pending:     'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  completed:   'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
  cancelled:   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const ACTION_COLORS: Record<string, string> = {
  created:          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  updated:          'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
  status_changed:   'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  assigned:         'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  moved_to_storage: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  uploaded:         'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
  scheduled:        'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  deleted:          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const ACTION_BORDER: Record<string, string> = {
  created:          'border-l-green-400 dark:border-l-green-600',
  updated:          'border-l-violet-400 dark:border-l-violet-600',
  status_changed:   'border-l-purple-400 dark:border-l-purple-600',
  assigned:         'border-l-amber-400 dark:border-l-amber-600',
  moved_to_storage: 'border-l-teal-400 dark:border-l-teal-600',
  uploaded:         'border-l-violet-400 dark:border-l-violet-600',
  scheduled:        'border-l-orange-400 dark:border-l-orange-600',
  deleted:          'border-l-red-400 dark:border-l-red-600',
};

// ── History Dialog ────────────────────────────────────────────────────────────

const FIELD_DISPLAY: Record<string, string> = {
  title: 'Title', shooting_category_id: 'Category', location: 'Location',
  date_time: 'Date & Time', page_id: 'Page', updated_page_id: 'Updated Page', status: 'Status',
};

function HistoryDialog({ activityId, title, open, onClose }: { activityId: string; title: string; open: boolean; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ['dm-team', 'activity-history', activityId],
    queryFn: () => dmTeamService.getActivityHistory(activityId),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>History — {title}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {(data?.data ?? []).length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No history yet</p>}
          {(data?.data ?? []).map((h: DmActivityHistory) => {
            const assignedVal = h.action_type === 'assigned' && h.new_value !== null
              ? (h.new_value as any) : null;
            const assignedUsers: string[] = Array.isArray(assignedVal?.users) ? assignedVal.users : [];
            const assignedPage: string | null = assignedVal?.page ?? null;
            const hasAssignedUsers = assignedUsers.length > 0;
            const hasFieldChange = !!(h.field_name && (h.old_value !== null || h.new_value !== null));
            const showDescription = !!(h.description && !hasAssignedUsers && h.action_type !== 'status_changed' && !hasFieldChange);

            return (
              <div key={h.id} className={`rounded-lg border border-l-4 ${ACTION_BORDER[h.action_type] ?? 'border-l-gray-300'} px-4 py-3`}>
                {/* Header: badge + timestamp + actor */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${ACTION_COLORS[h.action_type] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'}`}>
                    {h.action_type.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-muted-foreground">{format(new Date(h.created_at), 'd MMM yyyy, HH:mm')}</span>
                  <span className="text-xs font-semibold ml-auto shrink-0">{h.created_by_name}</span>
                </div>

                {/* Plain description (created / uploaded / scheduled / moved_to_storage) */}
                {showDescription && (
                  <p className="text-sm text-muted-foreground mt-2">{h.description}</p>
                )}

                {/* Assigned users */}
                {hasAssignedUsers && (
                  <div className="mt-2.5 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assigned to</p>
                    <div className="flex flex-wrap gap-1.5">
                      {assignedUsers.map((name: string) => (
                        <span key={name} className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400 font-medium border border-amber-200 dark:border-amber-800">
                          {name}
                        </span>
                      ))}
                    </div>
                    {assignedPage && (
                      <p className="text-xs text-muted-foreground">
                        Page section: <span className="font-semibold text-foreground">{assignedPage}</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Field change: label above, old → new pills */}
                {hasFieldChange && (
                  <div className="mt-2.5 space-y-1.5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {FIELD_DISPLAY[h.field_name!] ?? h.field_name}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm px-2.5 py-1 rounded-md font-medium ${h.old_value !== null ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 line-through' : 'text-muted-foreground'}`}>
                        {h.old_value !== null ? String(h.old_value) : '—'}
                      </span>
                      <span className="text-muted-foreground font-bold text-base">→</span>
                      <span className={`text-sm px-2.5 py-1 rounded-md font-medium ${h.new_value !== null ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'text-muted-foreground'}`}>
                        {h.new_value !== null ? String(h.new_value) : '—'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Assign Dialog ─────────────────────────────────────────────────────────────

function AssignDialog({ activity, open, onClose, assignList, pages }: {
  activity: DmActivity; open: boolean; onClose: () => void; assignList: any[]; pages: any[];
}) {
  const qc = useQueryClient();
  const [selectedUsers, setSelectedUsers] = useState<string[]>(activity.assigned_users.map(u => u.id));
  const [pageId, setPageId] = useState(activity.page?.id ?? '');

  const mutation = useMutation({
    mutationFn: (body: AssignDmActivityInput) => dmTeamService.assignActivity(activity.id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] }); onClose(); toast.success('Activity assigned'); },
    onError: () => toast.error('Failed to assign'),
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Assign — {activity.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Assign Users</Label>
            <div className="flex flex-wrap gap-1.5 mt-1 min-h-[32px] rounded-lg border p-2">
              {selectedUsers.map(uid => {
                const u = assignList.find((x: any) => x.id === uid);
                return u ? (
                  <Badge key={uid} variant="secondary" className="text-xs flex items-center gap-1">
                    {u.first_name} {u.last_name}
                    <button onClick={() => setSelectedUsers(s => s.filter(x => x !== uid))} className="ml-1 hover:text-destructive">×</button>
                  </Badge>
                ) : null;
              })}
            </div>
            <Select onValueChange={v => { if (!selectedUsers.includes(v)) setSelectedUsers(s => [...s, v]); }}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Add user…" /></SelectTrigger>
              <SelectContent>
                {assignList.filter((u: any) => !selectedUsers.includes(u.id)).map((u: any) => (
                  <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Page Section</Label>
            <Select value={pageId} onValueChange={setPageId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select page…" /></SelectTrigger>
              <SelectContent>
                {pages.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={mutation.isPending || selectedUsers.length === 0}
            onClick={() => mutation.mutate({ user_ids: selectedUsers, page_id: pageId || undefined })}
          >
            Save Assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Upload Dialog ─────────────────────────────────────────────────────────────

function UploadDialog({ activityId, open, onClose }: { activityId: string; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [link, setLink] = useState('');

  const mutation = useMutation({
    mutationFn: async (body: UploadDmActivityInput) => {
      await dmTeamService.uploadActivity(activityId, body);
      await dmTeamService.moveToStorage(activityId, true);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] });
      qc.invalidateQueries({ queryKey: ['dm-team', 'storage'] });
      onClose();
      setLink('');
      toast.success('Upload link saved & moved to storage');
    },
    onError: () => toast.error('Failed to save'),
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Upload Link</DialogTitle></DialogHeader>
        <div><Label>Upload URL</Label><Input value={link} onChange={e => setLink(e.target.value)} className="mt-1" placeholder="https://…" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!link.trim() || mutation.isPending} onClick={() => mutation.mutate({ upload_link: link.trim() })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Schedule Dialog ───────────────────────────────────────────────────────────

function ScheduleDialog({ activityId, open, onClose }: { activityId: string; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [date, setDate] = useState('');

  const mutation = useMutation({
    mutationFn: (body: ScheduleDmActivityInput) => dmTeamService.scheduleActivity(activityId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] }); onClose(); setDate(''); toast.success('Activity scheduled'); },
    onError: () => toast.error('Failed to schedule'),
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Schedule Activity</DialogTitle></DialogHeader>
        <div>
          <Label>Scheduled Date & Time</Label>
          <div className="mt-1">
            <DateTimePicker value={date || null} onChange={v => setDate(v || '')} minDate={startOfToday()} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!date || mutation.isPending} onClick={() => mutation.mutate({ scheduled_date: localToISO(date) })}>Schedule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Swap Page Dialog ──────────────────────────────────────────────────────────

function SwapPageDialog({ activity, open, onClose, pages }: {
  activity: DmActivity; open: boolean; onClose: () => void; pages: any[];
}) {
  const qc = useQueryClient();
  const currentPageId = (activity.updated_page as any)?.id ?? (activity.page as any)?.id ?? '';
  const [selectedPageId, setSelectedPageId] = useState(currentPageId);

  const mutation = useMutation({
    mutationFn: (pageId: string) => dmTeamService.updateActivity(activity.id, { updated_page_id: pageId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] });
      onClose();
      toast.success('Page updated');
    },
    onError: () => toast.error('Failed to update page'),
  });

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Swap Page — {activity.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Current Page</Label>
            <p className="text-sm text-muted-foreground px-3 py-2 rounded-md bg-muted">
              {(activity.updated_page as any)?.name ?? (activity.page as any)?.name ?? '—'}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>New Page</Label>
            <Select
              value={selectedPageId}
              onValueChange={setSelectedPageId}
              key={activity.id}
            >
              <SelectTrigger><SelectValue placeholder="Select page…" /></SelectTrigger>
              <SelectContent>
                {pages.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={mutation.isPending || !selectedPageId || selectedPageId === currentPageId}
            onClick={() => mutation.mutate(selectedPageId)}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Complete Activity Dialog ──────────────────────────────────────────────────

function CompleteActivityDialog({
  activity, open, onClose, pages,
}: {
  activity: DmActivity;
  open: boolean;
  onClose: () => void;
  pages: { id: string; name: string }[];
}) {
  const qc = useQueryClient();
  const [selectedPageId, setSelectedPageId] = useState(activity.updated_page?.id ?? '__none__');
  const [pageError, setPageError] = useState(false);

  const statusMutation = useMutation({
    mutationFn: () => dmTeamService.updateActivityStatus(activity.id, { status: 'completed' }),
  });
  const pageMutation = useMutation({
    mutationFn: (pageId: string) => dmTeamService.updateActivity(activity.id, { updated_page_id: pageId }),
  });

  const locationRequired = !activity.location?.trim();

  async function handleConfirm() {
    if (locationRequired) {
      toast.error('Location is required before completing this activity');
      return;
    }
    if (selectedPageId === '__none__') {
      setPageError(true);
      return;
    }
    try {
      await statusMutation.mutateAsync();
      const pageChanged = selectedPageId !== (activity.updated_page?.id ?? '__none__');
      if (pageChanged) await pageMutation.mutateAsync(selectedPageId);
      qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] });
      toast.success('Activity marked as completed');
      onClose();
    } catch {
      toast.error('Failed to complete activity');
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Complete Activity</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {locationRequired && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2.5 text-sm text-rose-500">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Location is required for this category. Edit the activity to add a location before completing.</span>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Current Page Section</Label>
            <p className="text-sm text-muted-foreground rounded-md bg-muted px-3 py-2">
              {activity.page?.name ?? '—'}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Update Page *</Label>
            <Select value={selectedPageId} onValueChange={v => { setSelectedPageId(v); setPageError(false); }}>
              <SelectTrigger className={pageError ? 'border-destructive focus:ring-destructive' : ''}>
                <SelectValue placeholder="Select page…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select a page —</SelectItem>
                {pages.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pageError && (
              <p className="text-xs text-destructive">Page selection is required</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={statusMutation.isPending || pageMutation.isPending || locationRequired}
            onClick={handleConfirm}
          >
            {statusMutation.isPending || pageMutation.isPending ? 'Completing…' : 'Confirm Complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Create Activity Dialog ────────────────────────────────────────────────────

const activityFormSchema = createDmActivitySchema.extend({
  date_time: z.string().min(1, 'Date & time is required'),
});
type ActivityFormData = z.infer<typeof activityFormSchema>;

function CreateActivityDialog({ open, onClose, shootingCategories, pages }: {
  open: boolean; onClose: () => void; shootingCategories: any[]; pages: any[];
}) {
  const qc = useQueryClient();
  const form = useForm<ActivityFormData>({ resolver: zodResolver(activityFormSchema) });

  const mutation = useMutation({
    mutationFn: (body: CreateDmActivityInput) => dmTeamService.createActivity(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] }); onClose(); form.reset(); toast.success('Activity created'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to create'),
  });

  const catId = form.watch('shooting_category_id');
  const requiresLocation = shootingCategories.find(c => c.id === catId)?.requires_location ?? false;

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (requiresLocation && !form.getValues('location')?.trim()) {
      form.setError('location', { message: 'Location is required for this category' });
    }
    form.handleSubmit((d) => {
      if (requiresLocation && !d.location?.trim()) return;
      mutation.mutate({ ...d, date_time: localToISO(d.date_time) } as CreateDmActivityInput);
    })(e);
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Activity</DialogTitle></DialogHeader>
        <form
          onSubmit={handleFormSubmit}
          className="space-y-4"
        >
          <div><Label>Title *</Label><Input {...form.register('title')} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select onValueChange={v => form.setValue('shooting_category_id', v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{shootingCategories.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date & Time *</Label>
              <DateTimePicker
                value={form.watch('date_time') || null}
                onChange={v => form.setValue('date_time', v || '', { shouldValidate: true })}
                minDate={startOfToday()}
              />
              {form.formState.errors.date_time && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.date_time.message}</p>
              )}
            </div>
          </div>
          <div>
            <Label>Location {requiresLocation ? '*' : '(optional)'}</Label>
            <Input {...form.register('location')} className="mt-1" placeholder="Location / NAS path…" />
            {form.formState.errors.location && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.location.message}</p>
            )}
          </div>
          <div>
            <Label>Page Section</Label>
            <Select onValueChange={v => form.setValue('page_id', v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select page…" /></SelectTrigger>
              <SelectContent>{pages.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending || (requiresLocation && !form.watch('location')?.trim())}>Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Activities Page ──────────────────────────────────────────────────────

function MobileActivityCard({ row, isUploadTab, canManage, canCreate, canCompleteListDelete, onHistory, onEdit, onAssign, onDelete }: {
  row: DmActivity;
  /** Upload tab rows are finished: History, the link and Delete only. */
  isUploadTab: boolean;
  canManage: boolean;
  canCreate: boolean;
  canCompleteListDelete: boolean;
  onHistory: (v: { id: string; title: string }) => void;
  onEdit: (row: DmActivity) => void;
  onAssign: (row: DmActivity) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium truncate flex-1">{row.title}</p>
        <Badge className={`text-xs shrink-0 ${STATUS_COLORS[row.status]}`}>{STATUS_LABELS[row.status]}</Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        {format(new Date(row.date_time), 'd MMM yyyy, HH:mm')}
        {row.shooting_category?.name && ` · ${row.shooting_category.name}`}
        {row.page?.name && ` · ${row.page.name}`}
      </p>
      {row.location && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <MapPin className="h-3 w-3" />{row.location}
        </p>
      )}
      {row.scheduled_date && (
        <p className="text-xs font-medium text-orange-700 dark:text-orange-400 flex items-center gap-1">
          <Calendar className="h-3 w-3" />Scheduled: {format(new Date(row.scheduled_date), 'd MMM yyyy, HH:mm')}
        </p>
      )}
      <div className="flex gap-1 pt-0.5">
        <Button size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8" title="History"
          onClick={() => onHistory({ id: row.id, title: row.title })}>
          <History className="h-4 w-4" />
        </Button>
        {isUploadTab && row.upload_link && isHttpUrl(row.upload_link) && (
          <Button asChild size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8 text-teal-700 dark:text-teal-400" title="Open upload link">
            <a href={row.upload_link} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a>
          </Button>
        )}
        {canManage && row.status !== 'completed' && !isUploadTab && (
          <>
            {!isActivityLocked(row) && (
              <>
                <Button size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8 text-teal-600 hover:text-teal-700" title="Edit"
                  onClick={() => onEdit(row)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8" title="Assign"
                  onClick={() => onAssign(row)}>
                  <UserPlus className="h-4 w-4" />
                </Button>
              </>
            )}
            {canCreate && (
              <Button size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive" title="Delete"
                onClick={() => onDelete(row.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
        {canManage && (row.status === 'completed' ? canCompleteListDelete : isUploadTab && canCreate) && (
          <Button size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive" title="Delete"
            onClick={() => onDelete(row.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function ActivitiesPage() {
  const qc = useQueryClient();
  const permission = usePermission();
  const canManage = permission.has(PERMISSIONS.DM_TEAM_ACTIVITIES_MANAGE);

  const [pageTab, setPageTab] = useState('');
  const [statusTab, setStatusTab] = useState<ActivityTab>('active');
  const isUploadTab = statusTab === 'uploaded';
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [editActivity, setEditActivity] = useState<DmActivity | null>(null);
  const [historyActivity, setHistoryActivity] = useState<{ id: string; title: string } | null>(null);
  const [assignActivity, setAssignActivity] = useState<DmActivity | null>(null);
  const [uploadActivityId, setUploadActivityId] = useState<string | null>(null);
  const [scheduleActivityId, setScheduleActivityId] = useState<string | null>(null);
  const [swapPageActivity, setSwapPageActivity] = useState<DmActivity | null>(null);
  const [completeActivity, setCompleteActivity] = useState<DmActivity | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [cancelActivity, setCancelActivity] = useState<DmActivity | null>(null);

  const { data: metaData } = useQuery({
    queryKey: ['dm-team', 'activities-meta'],
    queryFn: () => dmTeamService.getActivitiesMeta(),
  });
  const canCreate = metaData?.data?.hasManageAccess ?? false;
  const canCompleteListDelete = metaData?.data?.hasCompleteListDeleteAccess ?? false;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dm-team', 'activities', { pageTab, statusTab, search, dateFrom, dateTo, page }],
    queryFn: () => dmTeamService.listActivities({
      page,
      limit: 20,
      q: search || undefined,
      page_id: pageTab || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      // The bucket owns the storage rule (Upload tab = uploaded/in storage), so no is_in_storage here.
      bucket: statusTab,
    }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDmActivityStatusInput }) =>
      dmTeamService.updateActivityStatus(id, body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] });
      // Cancelled activities move to Storage.
      qc.invalidateQueries({ queryKey: ['dm-team', 'storage'] });
      qc.invalidateQueries({ queryKey: ['dm-team', 'calendar'] });
      toast.success(vars.body.status === 'cancelled' ? 'Activity cancelled — moved to Storage' : 'Status updated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to update status'),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDmActivityInput }) =>
      dmTeamService.updateActivity(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] });
      setEditActivity(null);
      toast.success('Activity updated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dmTeamService.deleteActivity(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] }); setDeleteId(null); toast.success('Activity deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to delete'),
  });

  const pages = metaData?.data?.pages ?? [];
  const shootingCategories = metaData?.data?.shootingCategories ?? [];
  const assignList = metaData?.data?.assignList ?? [];
  const pagination = data?.pagination;

  const COLUMNS: Column<DmActivity>[] = [
    {
      key: 'title',
      header: 'Title',
      cell: row => <span className="font-medium max-w-[140px] truncate block" title={row.title}>{row.title}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      cell: row => <span className="text-xs">{row.shooting_category?.name ?? '—'}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      cell: row => row.location ? (
        <span className="flex items-center gap-1 text-sm">
          <MapPin className="w-3 h-3 text-purple-500 shrink-0" />
          <span className="truncate max-w-[90px]">{row.location}</span>
        </span>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'date_time',
      // Schedule tab: the posting date (orange) sits under the shoot date — no extra column, so the table fits.
      header: 'Date & Time',
      cell: row => (
        <div className="text-xs whitespace-nowrap">
          <span>{format(new Date(row.date_time), 'd MMM, HH:mm')}</span>
          {statusTab === 'scheduled' && row.scheduled_date && (
            <span className="mt-0.5 flex items-center gap-1 font-medium text-orange-700 dark:text-orange-400" title="Scheduled">
              <Calendar className="h-3 w-3" />{format(new Date(row.scheduled_date), 'd MMM, HH:mm')}
            </span>
          )}
        </div>
      ),
      className: 'whitespace-nowrap',
    },
    ...(isUploadTab ? [{
      key: 'upload_link',
      header: 'Upload Link',
      cell: (row: DmActivity) => !row.upload_link ? (
        <span className="text-xs text-muted-foreground">In storage</span>
      ) : !isHttpUrl(row.upload_link) ? (
        // Old rows hold non-URL text ("a", "123"); a relative href would navigate inside the app.
        <span className="text-xs text-muted-foreground truncate max-w-[120px] block" title={row.upload_link}>{row.upload_link}</span>
      ) : (
        <a
          href={row.upload_link} target="_blank" rel="noopener noreferrer"
          title={row.upload_link}
          className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline dark:text-teal-400"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Open
        </a>
      ),
    }] : []),
    {
      key: 'status',
      header: 'Status',
      cell: row => (canManage && row.status !== 'completed' && !isActivityLocked(row) && !isUploadTab) ? (
        <Select
          value={row.status}
          onValueChange={v => {
            if (v === 'completed') {
              if (!row.location?.trim()) {
                toast.error('Location is required before completing this activity');
                return;
              }
              setCompleteActivity(row);
            } else if (v === 'cancelled') {
              setCancelActivity(row);
            } else {
              updateStatusMutation.mutate({ id: row.id, body: { status: v as DmActivityStatus } });
            }
          }}
        >
          <SelectTrigger className={`h-7 text-xs w-32 border-0 ${STATUS_COLORS[row.status]}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(STATUS_LABELS) as DmActivityStatus[]).map(s => (
              <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Badge className={`text-xs ${STATUS_COLORS[row.status]}`}>{STATUS_LABELS[row.status]}</Badge>
      ),
    },
    {
      key: 'assigned_users',
      header: 'Users',
      cell: row => row.assigned_users.length > 0
        ? <Badge variant="secondary" className="text-xs">{row.assigned_users.length}</Badge>
        : <span className="text-muted-foreground">—</span>,
      className: 'w-16',
    },
    {
      key: 'page',
      header: 'Page Section',
      cell: row => <span className="text-xs truncate max-w-[80px] block">{row.page?.name ?? '—'}</span>,
    },
    {
      key: 'updated_page',
      header: 'Updated Page',
      cell: row => <span className="text-xs truncate max-w-[80px] block">{row.updated_page?.name ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      cell: row => (
        <div className="flex gap-0.5">
          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" title="History"
            onClick={() => setHistoryActivity({ id: row.id, title: row.title })}>
            <History className="h-4 w-4" />
          </Button>
          {isUploadTab ? (
            // Uploaded = finished: only History and Delete.
            canManage && (row.status === 'completed' ? canCompleteListDelete : canCreate) && (
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive" title="Delete"
                onClick={() => setDeleteId(row.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )
          ) : canManage && row.status === 'completed' ? (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" title="Upload Link"
                onClick={() => setUploadActivityId(row.id)}>
                <Upload className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" title="Schedule"
                onClick={() => setScheduleActivityId(row.id)}>
                <Calendar className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" title="Swap Page"
                onClick={() => setSwapPageActivity(row)}>
                <ArrowLeftRight className="h-4 w-4" />
              </Button>
              {canCompleteListDelete && (
                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive" title="Delete"
                  onClick={() => setDeleteId(row.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </>
          ) : canManage ? (
            <>
              {!isActivityLocked(row) && (
                <>
                  <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-teal-600 hover:text-teal-700" title="Edit"
                    onClick={() => setEditActivity(row)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" title="Assign"
                    onClick={() => setAssignActivity(row)}>
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </>
              )}
              {canCreate && (
                <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive" title="Delete"
                  onClick={() => setDeleteId(row.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </>
          ) : null}
        </div>
      ),
      className: 'w-32',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">DM Activities</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Activity flow management</p>
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> New Activity
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Tabs
          tabs={[
            { key: 'active',        label: 'Active' },
            { key: 'completed',     label: 'Complete' },
            { key: 'scheduled',     label: 'Schedule' },
            { key: 'uploaded',      label: 'Upload' },
            { key: 'not_completed', label: 'Not Completed' },
          ]}
          active={statusTab}
          onChange={v => { setStatusTab(v as ActivityTab); setPage(1); setPageTab(''); }}
        />
        <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search…" className="max-w-xs" />
        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => { setDateFrom(f); setDateTo(t); setPage(1); }}
          placeholder="Filter by date range"
          className="w-52"
        />
      </div>

      <Tabs
        tabs={[
          { key: '', label: 'All' },
          ...pages.map((p: any) => ({ key: p.id, label: p.name })),
        ]}
        active={pageTab}
        onChange={v => { setPageTab(v); setPage(1); }}
      />

      <div className="hidden md:block">
        <DataTable
          columns={COLUMNS}
          rows={(data?.data ?? [])}
          rowKey={r => r.id}
          loading={isLoading}
          error={isError}
          onRetry={refetch}
          empty={
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">{TAB_EMPTY_TEXT[statusTab]}</p>
              {canManage && statusTab === 'active' && (
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setCreateOpen(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Create first activity
                </Button>
              )}
            </div>
          }
          pagination={pagination ? {
            page: pagination.page,
            totalPages: pagination.totalPages,
            total: pagination.total,
            onPageChange: setPage,
          } : undefined}
        />
      </div>
      <div className="block md:hidden space-y-2">
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (data?.data ?? []).length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">{TAB_EMPTY_TEXT[statusTab]}</p>
          </div>
        ) : (data?.data ?? []).map(row => (
          <MobileActivityCard key={row.id} row={row} isUploadTab={isUploadTab} canManage={canManage} canCreate={canCreate} canCompleteListDelete={canCompleteListDelete}
            onHistory={setHistoryActivity} onEdit={setEditActivity} onAssign={setAssignActivity}
            onDelete={setDeleteId} />
        ))}
      </div>

      <CreateActivityDialog open={createOpen} onClose={() => setCreateOpen(false)} shootingCategories={shootingCategories} pages={pages} />

      {editActivity && (
        <EditActivityDialog
          key={editActivity.id}
          activity={editActivity}
          open={!!editActivity}
          onClose={() => setEditActivity(null)}
          shootingCategories={shootingCategories}
          pages={pages}
          onSubmit={body => editMutation.mutate({ id: editActivity.id, body })}
          pending={editMutation.isPending}
        />
      )}

      {historyActivity && (
        <HistoryDialog activityId={historyActivity.id} title={historyActivity.title} open={!!historyActivity} onClose={() => setHistoryActivity(null)} />
      )}
      {assignActivity && (
        <AssignDialog activity={assignActivity} open={!!assignActivity} onClose={() => setAssignActivity(null)} assignList={assignList} pages={pages} />
      )}
      {uploadActivityId && (
        <UploadDialog activityId={uploadActivityId} open={!!uploadActivityId} onClose={() => setUploadActivityId(null)} />
      )}
      {scheduleActivityId && (
        <ScheduleDialog activityId={scheduleActivityId} open={!!scheduleActivityId} onClose={() => setScheduleActivityId(null)} />
      )}
      {swapPageActivity && (
        <SwapPageDialog activity={swapPageActivity} open={!!swapPageActivity} onClose={() => setSwapPageActivity(null)} pages={pages} />
      )}
      {completeActivity && (
        <CompleteActivityDialog
          activity={completeActivity}
          open={!!completeActivity}
          onClose={() => setCompleteActivity(null)}
          pages={pages}
        />
      )}

      <AlertDialog open={!!deleteId} onOpenChange={v => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete activity?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteId && deleteMutation.mutate(deleteId)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cancelActivity} onOpenChange={v => !v && setCancelActivity(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{CANCEL_CONFIRM_TITLE}</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{cancelActivity?.title}</span> — {CANCEL_CONFIRM_TEXT}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep activity</AlertDialogCancel>
            <AlertDialogAction
              // This app's AlertDialogAction is a plain button — close the confirm ourselves.
              onClick={() => {
                if (cancelActivity) updateStatusMutation.mutate({ id: cancelActivity.id, body: { status: 'cancelled' } });
                setCancelActivity(null);
              }}
              className="bg-destructive text-destructive-foreground"
            >
              Cancel activity
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
